import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { AppComponentProps } from '../../../typescriptHelpers';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { MobileOutlined, ThunderboltFilled } from '@ant-design/icons';
import Divider from 'antd/lib/divider';
import Table from 'antd/lib/table';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectAppProvisioningProfiles } from '../../../selectors/selectors';
import { ProvisioningProfile, ProvisioningProfileState } from '../../../types';
import { displayTextForProvisioningProfileType } from '../../../helpers/displayHelpers';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../../timeHelpers';
import { ProfileDetailsDrawer } from './ProfileDetailsDrawer';
import { SortTableButton } from '../../tableFilters/SortTableButton';
import Tooltip from 'antd/lib/tooltip';
import { useAppPlatform } from '@/utils/hooks/helpers';

const renderTitle = (title: string) => {
    return <Text strong>{title}</Text>;
};

export type ProfilesSectionProps = AppComponentProps;

enum SortType {
    Name = 'name',
    CreationDate = 'creationDate',
    ExpirationDate = 'expirationDate',
    ProfileType = 'profileType',
    DeviceCount = 'deviceCount',
}

export const ProfilesSection: React.FC<ProfilesSectionProps> = ({ appId, orgId }) => {
    const [selectedSortType, setSelectedSortType] = React.useState<SortType>(SortType.Name);

    const [highlightedProfile, setHighlightedProfile] = React.useState<ProvisioningProfile | null>(null);

    const provisioningProfiles = useSelector((state: RootState) => selectAppProvisioningProfiles(state, appId));
    const appPlatform = useAppPlatform({ appId, orgId });

    const activeProfiles = React.useMemo(() => {
        return provisioningProfiles?.filter((profile) => {
            return profile.profileState === ProvisioningProfileState.active;
        });
    }, [provisioningProfiles]);

    const sortedProvisioningProfiles = React.useMemo(() => {
        const sortedProfiles = [...(activeProfiles ?? [])];

        switch (selectedSortType) {
            case SortType.Name:
                sortedProfiles.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                });
                break;
            case SortType.CreationDate:
                sortedProfiles.sort((a, b) => {
                    return dayjs(a.creationDate).diff(dayjs(b.creationDate));
                });
                break;
            case SortType.ExpirationDate:
                sortedProfiles.sort((a, b) => {
                    return dayjs(a.expirationDate).diff(dayjs(b.expirationDate));
                });
                break;
            case SortType.ProfileType:
                sortedProfiles.sort((a, b) => {
                    return a.profileType.localeCompare(b.profileType);
                });
                break;
            case SortType.DeviceCount:
                sortedProfiles.sort((a, b) => {
                    return (a.devices?.length || 0) - (b.devices?.length || 0);
                });
                break;
            default:
                break;
        }

        return sortedProfiles;
    }, [activeProfiles, selectedSortType]);

    return (
        <div
            style={{
                width: '100%',
                marginTop: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 32,
                alignItems: 'stretch',
            }}
        >
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Space
                    split={
                        <Divider
                            style={{
                                margin: 0,
                            }}
                            type="vertical"
                        />
                    }
                >
                    <Text
                        style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: antGrey.grey8,
                        }}
                    >
                        Active profiles
                    </Text>
                </Space>
                <Space split={<Divider style={{ margin: 0 }} type="vertical" />}>
                    <SortTableButton<SortType>
                        selectedSortType={selectedSortType}
                        sortOptions={[
                            {
                                sortType: SortType.Name,
                                text: 'Name',
                            },
                            {
                                sortType: SortType.CreationDate,
                                text: 'Creation date',
                            },
                            {
                                sortType: SortType.ExpirationDate,
                                text: 'Expiration date',
                            },
                            {
                                sortType: SortType.ProfileType,
                                text: 'Profile type',
                            },
                            {
                                sortType: SortType.DeviceCount,
                                text: 'Device count',
                            },
                        ]}
                        onSelectSort={(sortType) => {
                            setSelectedSortType(sortType);
                        }}
                    />
                </Space>
            </div>
            <Table
                dataSource={sortedProvisioningProfiles?.map((profile) => ({
                    ...profile,
                    key: profile.id,
                }))}
                locale={{
                    emptyText: 'No profiles yet.',
                }}
                onRow={(record) => {
                    return {
                        onClick: () => {
                            setHighlightedProfile(record);
                        },
                    };
                }}
                columns={[
                    {
                        title: renderTitle('Profile name'),
                        key: 'name',
                        render: (profile: ProvisioningProfile) => {
                            return profile.name;
                        },
                    },
                    {
                        title: renderTitle('Profile type'),
                        key: 'profileType',
                        render: (profile: ProvisioningProfile) => {
                            return displayTextForProvisioningProfileType(profile.profileType);
                        },
                    },
                    {
                        title: renderTitle('Created on'),
                        key: 'creationDate',
                        render: (profile: ProvisioningProfile) => {
                            return profile?.createdByRunwayAt ?
                                    <Tooltip title={'This profile was created by Runway.'}>
                                        <Space>
                                            <ThunderboltFilled style={{ color: runwayColors.primary }} />
                                            <Text>
                                                {dayjs(profile?.creationDate).format(DatetimeFormat.ShortMonthDateYear)}
                                            </Text>
                                        </Space>
                                    </Tooltip>
                                :   <Text>
                                        {dayjs(profile?.creationDate).format(DatetimeFormat.ShortMonthDateYear)}
                                    </Text>;
                        },
                    },
                    {
                        title: renderTitle('Expiry'),
                        key: 'expirationDate',
                        render: (profile: ProvisioningProfile) => {
                            const expirationDateMoment = dayjs(profile.expirationDate);
                            return (
                                <Space>
                                    <Text>{expirationDateMoment.format(DatetimeFormat.ShortMonthDateYear)}</Text>
                                    <Text type="secondary">
                                        {expirationDateMoment.diff(dayjs(), 'days') > 0 ?
                                            `(${expirationDateMoment.diff(dayjs(), 'days')} days left)`
                                        :   '(expired)'}
                                    </Text>
                                </Space>
                            );
                        },
                    },
                    {
                        title: renderTitle('Devices'),
                        key: 'devices',
                        align: 'right',
                        render: (profile: ProvisioningProfile) => {
                            const deviceCount = profile.devices?.length || 0;
                            return (
                                <Space>
                                    {deviceCount > 0 ?
                                        <Text>{deviceCount}</Text>
                                    :   null}
                                    <MobileOutlined
                                        style={{
                                            color: deviceCount > 0 ? runwayColors.primary : antGrey.grey5,
                                        }}
                                    />
                                </Space>
                            );
                        },
                    },
                ]}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
            />
            <ProfileDetailsDrawer
                appId={appId}
                orgId={orgId}
                platform={appPlatform}
                profile={highlightedProfile}
                onClose={() => setHighlightedProfile(null)}
            />
        </div>
    );
};
